<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import { pluralize } from "common/utils/format/word";

const store = useStore();

const assumptiveQty = computed(() => store.getters["product/assumptiveQuantity"]);
const amount = computed(() => pluralize(assumptiveQty.value, "piece"));
const progress = computed(() => (assumptiveQty.value / 50) * 100);
const trackStyle = computed(() => ({
  background: `linear-gradient(to right, #121926 ${progress.value}%, #e3e8ef ${progress.value}%)`
}));
const onChange = e => {
  store.dispatch("product/updateAssumptiveQuantity", e.target.value);
};

const defaultVariant = computed(() => store.getters["product/assumptiveDefaultVariant"]);
const savings = computed(() => store.getters["product/assumptiveSavings"]);
const percentage = computed(() => store.getters["product/assumptiveSavingsPercentage"]);
</script>

<template>
  <div v-if="defaultVariant" class="assumptive-input">
    <div class="assumptive-input__title">Price Preview</div>
    <div class="assumptive-input__discount">
      <div class="assumptive-input__amount">{{ amount }}</div>
      <div class="assumptive-input__percentage">{{ percentage }}% Off</div>
      <div class="assumptive-input__savings">You save {{ n(savings, "currencyNoCents") }}</div>
    </div>
    <input
      class="assumptive-input__range-input"
      :style="trackStyle"
      type="range"
      min="1"
      max="50"
      :value="assumptiveQty"
      @input="onChange"
    />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";

.assumptive-input {
  color: $pod-gray-modern-600;
  padding: 16px 16px 0;

  &__title {
    font-size: 13px;
    font-weight: 500;
  }

  &__discount {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $pod-gray-modern-900;
  }

  &__amount {
    font-size: 20px;
    font-weight: 500;
    flex-basis: 100px;
  }

  &__percentage {
    font-size: 20px;
    font-weight: 600;
    color: $pod-green-text;
    background: $pod-green-bg;
    padding: 4px;
    border-radius: 6px;
    flex-basis: 90px;
    text-align: center;
  }

  &__savings {
    font-size: 14px;
    font-weight: 500;
  }

  &__range-input {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    cursor: pointer;
    outline: none;
    border-radius: 15px;
    height: 3px;
    background: $pod-gray-modern-200;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
    }

    &:focus {
      outline: none;
    }

    /* Special styling for WebKit/Blink */
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      height: 24px;
      width: 24px;
      background-color: $pod-gray-modern-900;
      border-radius: 50%;
      border: 3px solid $white;
      box-shadow: 0 4px 4px $pod-geyser;
    }

    /* All the same stuff for Firefox */
    &::-moz-range-thumb {
      height: 24px;
      width: 24px;
      background-color: $pod-gray-modern-900;
      border-radius: 50%;
      border: 3px solid $white;
      box-shadow: 0 4px 4px $pod-geyser;
    }
  }
}
</style>
